import React from "react";
import {
    Admin,
    AppBar,
    AppBarProps,
    CustomRoutes,
    Layout,
    LayoutProps,
    LocalesMenuButton,
    Logout,
    Menu,
    Resource,
    UserMenu,
    useTranslate,
} from "react-admin";
import {Route} from "react-router-dom";

import {Divider, Typography} from "@mui/material";
import "./App.css";
import authProvider from "./authProvider";
import {dataProvider} from "./dataProvider";
import {i18nProvider} from "./i18nProvider";
import Login from "./pages/login/Login";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import PreviewPage from "./pages/preview/PreviewPage";
import MainPageCreatorPage from "./pages/mainPageCreator/MainPageCreatorPage";
import FaqCreatorPage from "./pages/faqCreator/FaqCreatorPage";
import ServicesReorder from "./pages/services/ServicesReorder/ServicesReorder";
import SocialsEdit from "./pages/socials/SocialsEdit/SocialsEdit";
import SocialsCreate from "./pages/socials/SocialsCreate/SocialsCreate";
import SocialsList from "./pages/socials/SocialsList/SocialsList";
import BannersCreate from "./pages/banners/BannersCreate/BannersCreate";
import BannersList from "./pages/banners/BannersList/BannersList";
import BannersEdit from "./pages/banners/BannersEdit/BannersEdit";
import FormsCreate from "./pages/forms/FormsCreate/FormsCreate";
import FormsEdit from "./pages/forms/FormsEdit/FormsEdit";
import FormsList from "./pages/forms/FormsList/FormsList";
import ServicesCreate from "./pages/services/ServicesCreate/ServicesCreate";
import ServicesEdit from "./pages/services/ServicesEdit/ServicesEdit";
import ServicesList from "./pages/services/ServicesList/ServicesList";
import LayoutCreate from "./pages/layout/LayoutCreate/LayoutCreate";
import LayoutEdit from "./pages/layout/LayoutEdit/LayoutEdit";
import LayoutList from "./pages/layout/LayoutList/LayoutList";
import FaqCreate from "./pages/Faq/FaqCreate/FaqCreate";
import FaqEdit from "./pages/Faq/FaqEdit/FaqEdit";
import FaqList from "./pages/Faq/FaqList/FaqList";
import BenefitsCreate from "./pages/benefits/BenefitsCreate/BenefitsCreate";
import BenefitsEdit from "./pages/benefits/BenefitsEdit/BenefitsEdit";
import BenefitsList from "./pages/benefits/BenefitsList/BenefitsList";
import MainPageEdit from "./pages/mainPage/MainPageEdit/MainPageEdit";
import MainPageCreate from "./pages/mainPage/MainPageCreate/MainPageCreate";
import MainPageList from "./pages/mainPage/MainPageList/MainPageList";
import AboutCreate from "./pages/about/AboutCreate/AboutCreate";
import AboutEdit from "./pages/about/AboutEdit/AboutEdit";
import AboutList from "./pages/about/AboutList/AboutList";
import PopularCreate from "./pages/popular/PopularCreate/PopularCreate";
import PopularEdit from "./pages/popular/PopularEdit/PopularEdit";
import PopularList from "./pages/popular/PopularList/PopularList";
import ReviewCreate from "./pages/reviews/ReviewCreate/ReviewCreate";
import ReviewEdit from "./pages/reviews/ServicesEdit/ReviewEdit";
import ReviewsList from "./pages/reviews/ServicesList/ReviewsList";

const MyAppBar = (props: AppBarProps) => (
    <AppBar {...props} userMenu={<CustomUserMenu/>}>
        <Typography flex="1" variant="h6" id="react-admin-title"></Typography>
        <LocalesMenuButton
            languages={[
                {locale: "pl", name: "Polski"},
                {locale: "ru", name: "Русский"},
            ]}
        />
    </AppBar>
);

const CustomUserMenu = () => {
    return (
        <UserMenu>
            <Logout/>
        </UserMenu>
    );
};

const MenuGroupLabel = ({translationKey}: { translationKey: string }) => {
    const translate = useTranslate();
    return (
        <Typography
            variant="overline"
            color="text.secondary"
            sx={{px: 2, pt: 1, display: "block"}}
        >
            {translate(translationKey)}
        </Typography>
    );
};

export const MyMenu = () => {
    return (
        <Menu sx={{"&.RaMenu-open": {width: 255}}}>
            <MenuGroupLabel translationKey="menuGroups.templates"/>
            <Menu.Item
                key="preview"
                to="/preview"
                primaryText="preview.menu"
                leftIcon={<DesignServicesIcon/>}
            />
            <Menu.Item
                key="mainPageCreator"
                to="/mainPageCreator"
                primaryText="mainPageCreator.menu"
                leftIcon={<DesignServicesIcon/>}
            />
            <Menu.Item
                key="faqCreator"
                to="/faqCreator"
                primaryText="faqCreator.menu"
                leftIcon={<DesignServicesIcon/>}
            />

            <Divider sx={{my: 1}}/>
            <MenuGroupLabel translationKey="menuGroups.modules"/>
            <Menu.ResourceItem key="mainPage" name="mainPage"/>
            <Menu.ResourceItem key="socials" name="socials"/>
            <Menu.ResourceItem key="banners" name="banners"/>
            <Menu.ResourceItem key="benefits" name="benefits"/>
            <Menu.ResourceItem key="forms" name="forms"/>
            <Menu.ResourceItem key="services" name="services"/>
            <Menu.ResourceItem key="layout" name="layout"/>
            <Menu.ResourceItem key="faq" name="faq"/>
            <Menu.ResourceItem key="about" name="about"/>
            <Menu.ResourceItem key="popular" name="popular"/>
            <Menu.ResourceItem key="review" name="review"/>
        </Menu>
    );
};

const MyLayout = (props: LayoutProps) => (
    <Layout
        sx={{
            ".RaLayout-content": {
                ml: 2,
            },
        }}
        {...props}
        appBar={MyAppBar}
        menu={MyMenu}
    />
);

function App() {
    return (
        <Admin
            loginPage={Login}
            dataProvider={dataProvider}
            authProvider={authProvider}
            i18nProvider={i18nProvider}
            layout={MyLayout}
        >
            <CustomRoutes>
                <Route path="/preview" element={<PreviewPage/>}/>
                <Route path="/mainPageCreator" element={<MainPageCreatorPage/>}/>
                <Route path="/faqCreator" element={<FaqCreatorPage/>}/>
                <Route path="/services/reorder" element={<ServicesReorder/>}/>
            </CustomRoutes>
            <Resource
                name="mainPage"
                edit={MainPageEdit}
                create={MainPageCreate}
                list={MainPageList}
                icon={FilterAltIcon}
            />
            <Resource
                name="socials"
                edit={SocialsEdit}
                create={SocialsCreate}
                list={SocialsList}
                icon={FilterAltIcon}
            />
            <Resource
                name="banners"
                create={BannersCreate}
                edit={BannersEdit}
                list={BannersList}
                icon={FilterAltIcon}
            />
            <Resource
                name="benefits"
                create={BenefitsCreate}
                edit={BenefitsEdit}
                list={BenefitsList}
                icon={FilterAltIcon}
            />
            <Resource
                name="forms"
                create={FormsCreate}
                edit={FormsEdit}
                list={FormsList}
                icon={FilterAltIcon}
            />
            <Resource
                name="services"
                create={ServicesCreate}
                edit={ServicesEdit}
                list={ServicesList}
                icon={FilterAltIcon}
            />
            <Resource
                name="layout"
                create={LayoutCreate}
                edit={LayoutEdit}
                list={LayoutList}
                icon={FilterAltIcon}
            />
            <Resource
                name="faq"
                create={FaqCreate}
                edit={FaqEdit}
                list={FaqList}
                icon={FilterAltIcon}
            />
            <Resource
                name="about"
                create={AboutCreate}
                edit={AboutEdit}
                list={AboutList}
                icon={FilterAltIcon}
            />
            <Resource
                name="popular"
                create={PopularCreate}
                edit={PopularEdit}
                list={PopularList}
                icon={FilterAltIcon}
            />
            <Resource
                name="review"
                create={ReviewCreate}
                edit={ReviewEdit}
                list={ReviewsList}
                icon={FilterAltIcon}
            />
        </Admin>
    );
}

export default App;
